import {tx, ptx, usePageText} from '../lib/page-text';
import { StreamDestinations } from '../components/StreamDestinations';
import { useBroadcastControl } from '../hooks/useBroadcastControl';
import { useBroadcastNavigation } from '../hooks/useBroadcastNavigation';
import { BroadcastNotice, EndBroadcastDialog } from '../components/BroadcastControl';
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "../lib/localized-router";
import { api } from "../lib/api-client";
import { getIceServers } from "../lib/ice";
import { useAuth } from "../lib/auth-store";
import { DashboardDialog } from "../components/DashboardDialog";
import { InteractiveBoard } from "../components/InteractiveBoard";
import { PresentationControls } from "../components/PresentationControls";
import { CatalogPage } from "./Catalog";
import {
  StudioIcon,
  CatalogIcon,
  ScenariosIcon,
  SettingsIcon,
} from "../components/icons";

type Scene = "camera" | "screen" | "camPip" | "slideCam";
type Panel = "destinations" | "sources" | "appearance" | "board" | "sell";
type State = "idle" | "connecting" | "live" | "error";
interface Stream {
  id: string;
  name: string;
  status: string;
  keys?: { key: string }[];
  blueprintConfig?: {
    features?: { whiteboard?: boolean };
    commerce?: { catalog?: boolean };
  };
}
const W = 1280,
  H = 720,
  FPS = 30;
const scenes: { id: Scene; label: string }[] = [
  { id: "camera", label: "Camera" },
  { id: "screen", label: "Screen" },
  { id: "camPip", label: "Screen + camera" },
  { id: "slideCam", label: "Slide + camera" },
];

export function StudioPage() {
  usePageText();
  const canEdit = useAuth((s) =>
    ["OWNER", "ADMIN", "MEMBER"].includes(s.user?.role ?? ""),
  );
  const streams = useQuery({
    queryKey: ["my-streams"],
    queryFn: async () => (await api.get<Stream[]>("/me/streams")).data,
    refetchInterval: 15000,
  });
  const [params] = useSearchParams();
  const [streamId, setStreamId] = useState("");
  const control = useBroadcastControl(streamId);
  const selected = streams.data?.find((s) => s.id === streamId),
    key = selected?.keys?.[0]?.key;
  const detail = useQuery({
    queryKey: ["stream", streamId],
    queryFn: async () =>
      (await api.get<Stream>(`/me/streams/${streamId}`)).data,
    enabled: !!streamId,
  });
  const boardEnabled = !!detail.data?.blueprintConfig?.features?.whiteboard,
    sellEnabled = !!detail.data?.blueprintConfig?.commerce?.catalog;
  const [scene, setScene] = useState<Scene>("camera"),
    [panel, setPanel] = useState<Panel>("sources"),
    [banner, setBanner] = useState("");
  const [cameraOn, setCameraOn] = useState(false),
    [screenOn, setScreenOn] = useState(false),
    [micOn, setMicOn] = useState(false),
    [slideUrl, setSlideUrl] = useState("");
  const [state, setState] = useState<State>("idle"),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(""),
    [endConfirm, setEndConfirm] = useState(false);
  const [cameraId, setCameraId] = useState(""),
    [micId, setMicId] = useState(""),
    [devices, setDevices] = useState<MediaDeviceInfo[]>([]),
    [level, setLevel] = useState(0);
  const [stats, setStats] = useState<{ kbps: number; rtt: number | null }>({
    kbps: 0,
    rtt: null,
  });
  const [copied, setCopied] = useState(false),
    [boardMain, setBoardMain] = useState(false),
    [boardVisited, setBoardVisited] = useState(false);
  const cam = useRef<MediaStream | null>(null),
    mic = useRef<MediaStream | null>(null),
    screen = useRef<MediaStream | null>(null),
    slide = useRef<HTMLImageElement | null>(null);
  const camVideo = useRef<HTMLVideoElement>(null),
    screenVideo = useRef<HTMLVideoElement>(null),
    canvas = useRef<HTMLCanvasElement>(null);
  const pc = useRef<RTCPeerConnection | null>(null),
    output = useRef<MediaStream | null>(null),
    sender = useRef<RTCRtpSender | null>(null);
  const epoch = useRef(0),
    alive = useRef(true),
    capturing = useRef(false),
    publishing = useRef(false),
    ticker = useRef<ReturnType<typeof setInterval> | null>(null),
    connectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const audioCtx = useRef<AudioContext | null>(null),
    meter = useRef(0),
    lastStats = useRef({ bytes: 0, time: 0 });
  const live = state === "live",
    connecting = state === "connecting",
    locked = live || connecting;
  useBroadcastNavigation(locked);
  useEffect(() => { if (control.query.data?.blocked && publishing.current) stopPublishing(); }, [control.query.data?.blocked]);
  const presentation = useQuery({
    queryKey: ["presentation", streamId],
    queryFn: async () =>
      (
        await api.get<{ mode: "video" | "board"; enabled: boolean }>(
          `/me/streams/${streamId}/presentation`,
        )
      ).data,
    enabled: live && boardEnabled,
    refetchInterval: 2000,
    retry: false,
  });
  useEffect(() => {
    if (live && presentation.data)
      setBoardMain(
        presentation.data.enabled && presentation.data.mode === "board",
      );
  }, [live, presentation.data]);
  const sceneReady =
    scene === "camera"
      ? cameraOn
      : scene === "screen" || scene === "camPip"
        ? screenOn
        : !!slideUrl;
  const viewerUrl = `${window.location.origin}/v1/events/${encodeURIComponent(streamId)}/share`;
  const controls = useRef({ scene, banner, cameraOn, screenOn });
  controls.current = { scene, banner, cameraOn, screenOn };

  useEffect(() => {
    if (!streamId && streams.data?.length) {
      const wanted = params.get("stream");
      const s =
        streams.data.find((x) => x.id === wanted && x.status !== "ARCHIVED") ??
        streams.data.find((x) => x.status !== "ARCHIVED");
      if (s) setStreamId(s.id);
    }
  }, [streams.data, streamId, params]);
  useEffect(() => {
    if (
      (panel === "board" && !boardEnabled) ||
      (panel === "sell" && !sellEnabled)
    )
      setPanel("sources");
    if (panel === "board" && boardEnabled) setBoardVisited(true);
  }, [panel, boardEnabled, sellEnabled]);
  useEffect(() => {
    if (!boardEnabled) setBoardMain(false);
  }, [boardEnabled]);
  useEffect(() => {
    let frame = 0;
    const c = canvas.current,
      ctx = c?.getContext("2d");
    if (!ctx) return;
    const draw = () => {
      const s = controls.current;
      ctx.fillStyle = "#11151c";
      ctx.fillRect(0, 0, W, H);
      let shown = false;
      if (s.scene === "camera" && s.cameraOn && camVideo.current?.videoWidth) {
        drawImage(ctx, camVideo.current, 0, 0, W, H, true);
        shown = true;
      }
      if (
        (s.scene === "screen" || s.scene === "camPip") &&
        s.screenOn &&
        screenVideo.current?.videoWidth
      ) {
        drawImage(ctx, screenVideo.current, 0, 0, W, H, false);
        shown = true;
      }
      if (s.scene === "slideCam" && slide.current?.complete) {
        drawImage(ctx, slide.current, 0, 0, W, H, false);
        shown = true;
      }
      if (
        shown &&
        (s.scene === "camPip" || s.scene === "slideCam") &&
        s.cameraOn &&
        camVideo.current?.videoWidth
      ) {
        ctx.fillStyle = "#fff";
        ctx.fillRect(W - 304, H - 184, 284, 164);
        drawImage(ctx, camVideo.current, W - 300, H - 180, 276, 156, true);
      }
      if (!shown) {
        ctx.fillStyle = "#aab4c4";
        ctx.font = "24px system-ui";
        ctx.textAlign = "center";
        ctx.fillText(
          s.scene === "camera"
            ? "Turn on your camera"
            : s.scene === "slideCam"
              ? "Add a slide image"
              : "Share your screen",
          W / 2,
          H / 2,
        );
        ctx.font = "16px system-ui";
        ctx.fillStyle = "#778395";
        ctx.fillText("Your output preview appears here", W / 2, H / 2 + 38);
      }
      if (shown && s.banner) {
        ctx.font = "600 24px system-ui";
        const width = Math.min(800, ctx.measureText(s.banner).width + 40);
        ctx.fillStyle = "rgba(8,16,30,.85)";
        ctx.fillRect(32, H - 90, width, 54);
        ctx.fillStyle = "#fff";
        ctx.textAlign = "left";
        ctx.fillText(s.banner, 52, H - 54, 760);
      }
      frame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, []);

  function stopPublishing() {
    epoch.current++;
    publishing.current = false;
    if (ticker.current) clearInterval(ticker.current);
    if (connectTimer.current) clearTimeout(connectTimer.current);
    ticker.current = null;
    connectTimer.current = null;
    const peer = pc.current;
    pc.current = null;
    sender.current = null;
    peer?.close();
    output.current?.getTracks().forEach((t) => t.stop());
    output.current = null;
    lastStats.current = { bytes: 0, time: 0 };
    if (alive.current) {
      setState("idle");
      setStats({ kbps: 0, rtt: null });
      setEndConfirm(false);
    }
  }
  function fail(message: string) {
    stopPublishing();
    if (alive.current) {
      setState("error");
      setError(message);
    }
  }
  function stopCamera() {
    cam.current?.getTracks().forEach((t) => t.stop());
    cam.current = null;
    if (camVideo.current) camVideo.current.srcObject = null;
    setCameraOn(false);
  }
  function stopScreen() {
    screen.current?.getTracks().forEach((t) => t.stop());
    screen.current = null;
    if (screenVideo.current) screenVideo.current.srcObject = null;
    setScreenOn(false);
  }
  function stopMeter() {
    cancelAnimationFrame(meter.current);
    void audioCtx.current?.close();
    audioCtx.current = null;
    setLevel(0);
  }
  function startMeter(stream: MediaStream) {
    stopMeter();
    try {
      const ac = new AudioContext();
      audioCtx.current = ac;
      const node = ac.createAnalyser();
      node.fftSize = 256;
      ac.createMediaStreamSource(stream).connect(node);
      const data = new Uint8Array(node.fftSize);
      let updated = 0;
      const tick = () => {
        if (!alive.current || audioCtx.current !== ac) return;
        meter.current = requestAnimationFrame(tick);
        if (performance.now() - updated < 100) return;
        updated = performance.now();
        node.getByteTimeDomainData(data);
        setLevel(
          Math.min(
            100,
            Math.sqrt(
              data.reduce((n, x) => n + (x - 128) ** 2, 0) / data.length,
            ) * 3,
          ),
        );
      };
      void ac.resume();
      tick();
    } catch {
      /* meter is optional */
    }
  }
  async function refreshDevices() {
    try {
      setDevices(await navigator.mediaDevices.enumerateDevices());
    } catch {
      /* names appear after permission */
    }
  }
  async function capture(
    kind: "camera" | "microphone" | "screen",
    deviceId?: string,
  ) {
    if (!canEdit || capturing.current) return;
    capturing.current = true;
    setBusy(kind);
    setError("");
    try {
      if (!navigator.mediaDevices)
        throw new Error(
          "This browser cannot capture media. Open Studio in a supported browser over HTTPS.",
        );
      const stream =
        kind === "screen"
          ? await navigator.mediaDevices.getDisplayMedia({
              video: true,
              audio: false,
            })
          : await navigator.mediaDevices.getUserMedia(
              kind === "camera"
                ? {
                    video: {
                      width: { ideal: 1280 },
                      height: { ideal: 720 },
                      ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
                    },
                    audio: false,
                  }
                : {
                    video: false,
                    audio: {
                      echoCancellation: true,
                      noiseSuppression: true,
                      ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
                    },
                  },
            );
      if (!alive.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      if (kind === "camera") {
        stopCamera();
        cam.current = stream;
        if (camVideo.current) {
          camVideo.current.srcObject = stream;
          await camVideo.current.play().catch(() => {});
        }
        setCameraOn(true);
        if (deviceId) setCameraId(deviceId);
        stream.getVideoTracks()[0].onended = () => {
          if (cam.current === stream) stopCamera();
        };
      } else if (kind === "screen") {
        stopScreen();
        screen.current = stream;
        if (screenVideo.current) {
          screenVideo.current.srcObject = stream;
          await screenVideo.current.play().catch(() => {});
        }
        setScreenOn(true);
        setScene(cam.current ? "camPip" : "screen");
        stream.getVideoTracks()[0].onended = () => {
          if (screen.current === stream) {
            stopScreen();
            setScene(cam.current ? "camera" : "screen");
          }
        };
      } else {
        const track = stream.getAudioTracks()[0];
        try {
          if (sender.current) await sender.current.replaceTrack(track);
        } catch (e) {
          stream.getTracks().forEach((t) => t.stop());
          throw e;
        }
        if (!alive.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        mic.current?.getTracks().forEach((t) => t.stop());
        mic.current = stream;
        setMicOn(true);
        if (deviceId) setMicId(deviceId);
        startMeter(stream);
        track.onended = () => {
          if (mic.current === stream) {
            setMicOn(false);
            stopMeter();
          }
        };
      }
      void refreshDevices();
    } catch (e: any) {
      if (alive.current)
        setError(
          e.name === "NotAllowedError"
            ? "Permission was declined. Allow access in your browser, then try again."
            : e.message || "Could not capture this source.",
        );
    } finally {
      capturing.current = false;
      if (alive.current) setBusy("");
    }
  }
  function toggleMic() {
    const track = mic.current?.getAudioTracks()[0];
    if (track && track.readyState === "live") {
      track.enabled = !track.enabled;
      setMicOn(track.enabled);
    } else void capture("microphone", micId);
  }
  async function uploadSlide(file: File) {
    if (
      !["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
      file.size > 10 * 1024 * 1024
    ) {
      setError("Choose a JPG, PNG or WebP image smaller than 10 MB.");
      return;
    }
    const url = URL.createObjectURL(file),
      img = new Image();
    img.onload = () => {
      if (!alive.current) {
        URL.revokeObjectURL(url);
        return;
      }
      if (slide.current) URL.revokeObjectURL(slide.current.src);
      slide.current = img;
      setSlideUrl(url);
      setScene("slideCam");
      setError("");
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      if (alive.current) setError("This image could not be opened.");
    };
    img.src = url;
  }
  async function goLive() {
    if (
      !canEdit ||
      !control.query.data || control.query.isError || control.query.data.blocked ||
      !key ||
      !sceneReady ||
      publishing.current ||
      streams.isError ||
      selected?.status === "ARCHIVED" ||
      selected?.status === "LIVE"
    )
      return;
    publishing.current = true;
    setState("connecting");
    setError("");
    const attempt = ++epoch.current;
    try {
      const iceServers = await getIceServers(key);
      if (epoch.current !== attempt || !alive.current) return;
      if (!canvas.current?.captureStream)
        throw new Error(
          "This browser cannot broadcast the Studio canvas. Use the stream Broadcast tab or a supported desktop browser.",
        );
      const outgoing = canvas.current.captureStream(FPS);
      output.current = outgoing;
      const peer = new RTCPeerConnection({ iceServers });
      pc.current = peer;
      outgoing.getVideoTracks().forEach((t) => peer.addTrack(t, outgoing));
      sender.current = peer.addTransceiver("audio", {
        direction: "sendonly",
      }).sender;
      const audio = mic.current?.getAudioTracks()[0];
      if (audio && audio.readyState === "live")
        await sender.current.replaceTrack(audio);
      if (epoch.current !== attempt) return;
      peer.addEventListener("iceconnectionstatechange", () => {
        if (pc.current !== peer) return;
        if (["connected", "completed"].includes(peer.iceConnectionState)) {
          if (connectTimer.current) clearTimeout(connectTimer.current);
          setState("live");
        }
        if (peer.iceConnectionState === "failed")
          fail(
            "The broadcast connection failed. Check your network and start again.",
          );
        if (peer.iceConnectionState === "disconnected") {
          setState("connecting");
          if (connectTimer.current) clearTimeout(connectTimer.current);
          connectTimer.current = setTimeout(() => {
            if (pc.current === peer)
              fail(
                "The broadcast disconnected. Your sources are ready for another attempt.",
              );
          }, 15000);
        }
      });
      connectTimer.current = setTimeout(() => {
        if (pc.current === peer)
          fail(
            "The broadcast could not connect. Check your connection and try again.",
          );
      }, 25000);
      await peer.setLocalDescription(await peer.createOffer());
      await new Promise<void>((resolve) => {
        if (peer.iceGatheringState === "complete") return resolve();
        const timer = setTimeout(resolve, 2000);
        peer.addEventListener("icegatheringstatechange", () => {
          if (peer.iceGatheringState === "complete") {
            clearTimeout(timer);
            resolve();
          }
        });
      });
      if (epoch.current !== attempt) return;
      const response = await fetch(
        `${window.location.origin}/rtc/v1/whip/?app=live&stream=${encodeURIComponent(key)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/sdp" },
          body: peer.localDescription?.sdp ?? "",
          signal: AbortSignal.timeout(15000),
        },
      );
      if (!response.ok)
        throw new Error(`Broadcast connection failed (${response.status}).`);
      const answer = await response.text();
      if (epoch.current !== attempt) return;
      await peer.setRemoteDescription({ type: "answer", sdp: answer });
      if (epoch.current !== attempt) return;
      ticker.current = setInterval(async () => {
        try {
          const reports = await peer.getStats();
          if (pc.current !== peer) return;
          let bytes = 0,
            rtt: number | null = null;
          reports.forEach((r) => {
            if (r.type === "outbound-rtp" && r.kind === "video")
              bytes += r.bytesSent ?? 0;
            if (
              r.type === "remote-inbound-rtp" &&
              r.kind === "video" &&
              r.roundTripTime != null
            )
              rtt = Math.round(r.roundTripTime * 1000);
          });
          const time = performance.now(),
            prev = lastStats.current;
          setStats({
            kbps: prev.time
              ? Math.max(
                  0,
                  Math.round(((bytes - prev.bytes) * 8) / (time - prev.time)),
                )
              : 0,
            rtt,
          });
          lastStats.current = { bytes, time };
        } catch {
          /* transient stats failure */
        }
      }, 1000);
    } catch (e: any) {
      if (epoch.current === attempt)
        fail(e.message || "Could not start broadcasting.");
    }
  }
  useEffect(() => {
    alive.current = true;
    void refreshDevices();
    return () => {
      alive.current = false;
      stopPublishing();
      cam.current?.getTracks().forEach((t) => t.stop());
      screen.current?.getTracks().forEach((t) => t.stop());
      mic.current?.getTracks().forEach((t) => t.stop());
      cancelAnimationFrame(meter.current);
      void audioCtx.current?.close();
      if (slide.current) URL.revokeObjectURL(slide.current.src);
    };
  }, []);
  const blocked = !canEdit
    ? "An editor role is required to broadcast."
    : !control.query.data || control.query.isError
      ? "Reload broadcast status before starting."
      : control.query.data.blocked ? "Allow a new broadcast before starting."
    : streams.isError
      ? "Reload your streams before broadcasting."
      : !key
        ? "Choose an available stream."
        : selected?.status === "ARCHIVED"
          ? "This stream is archived."
          : !sceneReady
            ? "Add the source required by your selected scene."
            : selected?.status === "LIVE" && !locked
              ? "This stream is already live. End its existing publisher before starting here."
              : "";
  return (
    <div className="studio-workspace">
      <div className="workspace-heading">
        <div>
          <div className="page-eyebrow">
            <StudioIcon />{tx("Broadcast workspace")}</div>
          <h1 className="h1">{tx("Studio")}</h1>
        </div>
        <div className="studio-live-actions">
          <span className={`badge ${live ? "danger" : ""}`} role="status">
            {live
              ? tx("Live")
              : connecting
                ? tx("Connecting…")
                : state === "error"
                  ? tx("Connection stopped")
                  : tx("Preview")}
          </span>
          {locked || control.query.data?.status === "LIVE" ? (
            <button
              className="btn danger"
              onClick={() =>
                connecting ? stopPublishing() : setEndConfirm(true)
              }
            >
              {connecting ? tx("Cancel connection") : tx("End stream")}
            </button>
          ) : (
            <button
              className="btn primary"
              disabled={!!blocked || !!busy}
              onClick={() => void goLive()}
            >{tx("Go live")}</button>
          )}
        </div>
      </div>
      <BroadcastNotice control={control} onEnd={() => { control.action.reset(); setEndConfirm(true); }} />
      {error && (
        <div className="error studio-message" role="alert">
          {error}
          <button
            className="btn ghost sm"
            aria-label={tx("Dismiss error")}
            onClick={() => setError("")}
          >
            ×
          </button>
        </div>
      )}
      {!canEdit && (
        <p className="commerce-note">{tx("You have read-only access. An editor can operate this Studio.")}</p>
      )}
      <div className="studio-stream-bar">
        <label htmlFor="studio-stream">{tx("Stream")}</label>
        <select
          className="input"
          id="studio-stream"
          value={streamId}
          disabled={locked || streams.isPending}
          onChange={(e) => {
            setStreamId(e.target.value);
            setBoardMain(false);
            setBoardVisited(false);
            setPanel("sources");
          }}
        >
          <option value="">{tx("Choose a stream")}</option>
          {streams.data
            ?.filter((s) => s.status !== "ARCHIVED")
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
        </select>
        {streamId && (
          <>
            <button
              className="btn outline sm"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(viewerUrl);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1800);
                } catch {
                  setError(
                    "Could not copy the link. Use Open viewer to view and copy the address.",
                  );
                }
              }}
            >
              {copied ? tx("Copied") : tx("Copy viewer link")}
            </button>
            <a
              className="btn ghost sm"
              href={viewerUrl}
              target="_blank"
              rel="noopener"
            >{tx("Open viewer ↗")}</a>
          </>
        )}
      </div>
      {streams.isError && (
        <p className="error" role="alert">{ptx("Streams could not load.{v0}{v1}", {v0: (" "), v1: (<button
            className="btn outline sm"
            onClick={() => void streams.refetch()}
          >{tx("Retry")}</button>)})}</p>
      )}
      {streams.isSuccess &&
        !streams.data?.some((s) => s.status !== "ARCHIVED") && (
          <p className="card">{ptx("Create a stream to choose where your broadcast goes.{v0}{v1}", {v0: (" "), v1: (<Link to="/streams?create=1">{tx("Create stream")}</Link>)})}</p>
        )}
      <div
        className={`studio-desk ${panel === "sell" ? "studio-desk-selling" : ""}`}
      >
        <div className="studio-stage-column">
          <div
            className={`studio-stage ${(boardMain || panel === "board") && boardEnabled ? "studio-board-main" : ""}`}
          >
            <div className="studio-output">
              <canvas
                ref={canvas}
                width={W}
                height={H}
                aria-label={tx("Broadcast output preview")}
              />
              <span className="studio-preview-label">
                {live ? tx("Live video output") : tx("Preview · Only you can see this")}
              </span>
            </div>
            {boardEnabled &&
              (boardVisited || panel === "board" || boardMain) && (
                <div
                  className="studio-board"
                  hidden={panel !== "board" && !boardMain}
                >
                  <InteractiveBoard key={streamId} streamId={streamId} />
                </div>
              )}
            <video ref={camVideo} muted playsInline hidden />
            <video ref={screenVideo} muted playsInline hidden />
          </div>
          {live && boardEnabled && (
            <PresentationControls
              streamId={streamId}
              onShow={(mode) => {
                setBoardMain(mode === "board");
                setPanel(mode === "board" ? "board" : "sources");
              }}
            />
          )}
          <div
            className="studio-toolbar"
            role="group"
            aria-label={tx("Capture controls")}
          >
            <button
              className={`btn ${cameraOn ? "primary" : "outline"}`}
              disabled={!canEdit || !!busy}
              aria-pressed={cameraOn}
              onClick={() =>
                cameraOn ? stopCamera() : void capture("camera", cameraId)
              }
            >
              {busy === "camera"
                ? tx("Opening camera…")
                : cameraOn
                  ? tx("Camera on")
                  : tx("Camera off")}
            </button>
            <button
              className={`btn ${micOn ? "primary" : "outline"}`}
              disabled={!canEdit || !!busy}
              aria-pressed={micOn}
              onClick={toggleMic}
            >
              {busy === "microphone"
                ? tx("Opening mic…")
                : micOn
                  ? tx("Mic on")
                  : tx("Mic off")}
            </button>
            <meter
              min="0"
              max="100"
              value={micOn ? level : 0}
              aria-label={tx("Microphone level")}
            />
            <button
              className={`btn ${screenOn ? "primary" : "outline"}`}
              disabled={
                !canEdit || !!busy || !navigator.mediaDevices?.getDisplayMedia
              }
              aria-pressed={screenOn}
              onClick={() => (screenOn ? stopScreen() : void capture("screen"))}
            >
              {busy === "screen"
                ? tx("Opening share…")
                : screenOn
                  ? tx("Stop sharing")
                  : tx("Share screen")}
            </button>
          </div>
          <div className="studio-scenes" role="group" aria-label={tx("Scene layout")}>
            {scenes.map((s) => (
              <button
                key={s.id}
                className={`studio-scene ${scene === s.id ? "selected" : ""}`}
                aria-pressed={scene === s.id}
                onClick={() => setScene(s.id)}
                disabled={!canEdit}
              >
                <span
                  className={"scene-illustration scene-" + s.id}
                  aria-hidden="true"
                >
                  <i />
                  <b />
                </span>
                <span>{s.label}</span>
              </button>
            ))}
          </div>
          {!sceneReady && (
            <p className="commerce-note" role="status">
              {locked
                ? tx("Your selected scene has no source. Viewers may see a blank frame.")
                : tx("Add a source for this scene before going live.")}
            </p>
          )}
          {!locked && blocked && <p className="muted">{blocked}</p>}
          {!micOn && (
            <p className="muted">{tx("Microphone is off. Screen audio is not captured.")}</p>
          )}
          <details className="studio-health">
            <summary>{tx("Connection details")}</summary>
            <div className="studio-stats">
              <span>{ptx("Output: {v0} × {v1} · {v2} fps", {v0: (W), v1: (H), v2: (FPS)})}</span>
              {live && <span>{ptx("Upload: {v0} kbps", {v0: (stats.kbps)})}</span>}
              {stats.rtt != null && <span>{ptx("Round trip: {v0} ms", {v0: (stats.rtt)})}</span>}
            </div>
          </details>
        </div>
        <aside className="studio-tools card">
          <div
            className="studio-panel-tabs"
            role="group"
            aria-label={tx("Studio tools")}
          >
            <button
              className="btn ghost sm"
              aria-pressed={panel === "sources"}
              onClick={() => setPanel("sources")}
            >{ptx("{v0}Sources", {v0: (<StudioIcon />)})}</button>
            <button className="btn outline" aria-pressed={panel === "destinations"} onClick={() => setPanel("destinations")}>{tx("Destinations")}</button>
            <button
              className="btn ghost sm"
              aria-pressed={panel === "appearance"}
              onClick={() => setPanel("appearance")}
            >{ptx("{v0}Appearance", {v0: (<SettingsIcon />)})}</button>
            {boardEnabled && (
              <button
                className="btn ghost sm"
                aria-pressed={panel === "board"}
                onClick={() => setPanel("board")}
              >{ptx("{v0}Board", {v0: (<ScenariosIcon />)})}</button>
            )}
            {sellEnabled && (
              <button
                className="btn ghost sm"
                aria-pressed={panel === "sell"}
                onClick={() => setPanel("sell")}
              >{ptx("{v0}Sell", {v0: (<CatalogIcon />)})}</button>
            )}
          </div>
          {panel === "destinations" && selected && <StreamDestinations streamId={selected.id} />}
          {panel === "sources" && (
            <div className="studio-panel-body">
              <h2>{tx("Your sources")}</h2>
              <p className="muted">{tx("Choose devices and prepare your scene.")}</p>
              <label className="label" htmlFor="studio-camera">{tx("Camera")}</label>
              <select
                className="input"
                id="studio-camera"
                value={cameraId}
                disabled={!!busy || !canEdit}
                onChange={(e) => {
                  setCameraId(e.target.value);
                  if (cameraOn) void capture("camera", e.target.value);
                }}
              >
                <option value="">{tx("Default camera")}</option>
                {devices
                  .filter((d) => d.kind === "videoinput")
                  .map((d, i) => (
                    <option key={d.deviceId || i} value={d.deviceId}>
                      {d.label || ptx("Camera {v0}", {v0: (i + 1)})}
                    </option>
                  ))}
              </select>
              <label className="label" htmlFor="studio-mic">{tx("Microphone")}</label>
              <select
                className="input"
                id="studio-mic"
                value={micId}
                disabled={!!busy || !canEdit}
                onChange={(e) => {
                  setMicId(e.target.value);
                  if (mic.current) void capture("microphone", e.target.value);
                }}
              >
                <option value="">{tx("Default microphone")}</option>
                {devices
                  .filter((d) => d.kind === "audioinput")
                  .map((d, i) => (
                    <option key={d.deviceId || i} value={d.deviceId}>
                      {d.label || ptx("Microphone {v0}", {v0: (i + 1)})}
                    </option>
                  ))}
              </select>
              <label
                className="studio-slide-drop"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (canEdit && e.dataTransfer.files.length === 1)
                    void uploadSlide(e.dataTransfer.files[0]);
                }}
              >
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  aria-label={tx("Slide image")}
                  disabled={!canEdit}
                  onChange={(e) => {
                    if (e.target.files?.[0])
                      void uploadSlide(e.target.files[0]);
                    e.target.value = "";
                  }}
                />
                {slideUrl ? (
                  <img src={slideUrl} alt={tx("Slide preview")} />
                ) : (
                  <ScenariosIcon />
                )}
                <strong>
                  {slideUrl ? tx("Replace slide image") : tx("Drop a slide image")}
                </strong>
                <span>{tx("or choose an image · JPG, PNG, WebP")}</span>
              </label>
              {slideUrl && (
                <button
                  className="btn ghost sm"
                  disabled={!canEdit}
                  onClick={() => {
                    if (slide.current) URL.revokeObjectURL(slide.current.src);
                    slide.current = null;
                    setSlideUrl("");
                  }}
                >{tx("Remove slide")}</button>
              )}
              {!navigator.mediaDevices?.getDisplayMedia && (
                <p className="commerce-note">{tx("Screen sharing is unavailable in this browser. Camera and slide scenes may still be available.")}</p>
              )}
              <details>
                <summary>{tx("How Studio works")}</summary>
                <p className="muted">{tx("Choose your stream, enable sources and select a scene. The central preview is your outgoing video. Press Go live when ready. Keep Studio open while broadcasting.")}</p>
              </details>
            </div>
          )}
          {panel === "appearance" && (
            <div className="studio-panel-body">
              <h2>{tx("Appearance")}</h2>
              <label className="label" htmlFor="studio-banner">{tx("Presenter name or caption")}</label>
              <input
                className="input"
                id="studio-banner"
                maxLength={40}
                value={banner}
                disabled={!canEdit}
                onChange={(e) => setBanner(e.target.value)}
                placeholder={tx("Add a name banner")}
              />
              <p className="muted">{tx("Shown over your video. Clear the field to hide it.")}</p>
            </div>
          )}
          {panel === "board" && (
            <div className="studio-panel-body">
              <h2>{tx("Interactive board")}</h2>
              <p className="muted">{tx("The board editor is shown with your video preview in the corner. During the broadcast, use Board main or Video main to switch the audience view.")}</p>
              {!live && (
                <p className="commerce-note">{tx("Audience layout controls appear when this Studio is live.")}</p>
              )}
            </div>
          )}
          {panel === "sell" && (
            <div className="studio-panel-body">
              <CatalogPage streamId={streamId} archived={!canEdit} />
            </div>
          )}
          {detail.isError && (
            <p className="error" role="alert">{ptx("Stream tools could not load.{v0}{v1}", {v0: (" "), v1: (<button
                className="btn outline sm"
                onClick={() => void detail.refetch()}
              >{tx("Retry tools")}</button>)})}</p>
          )}
        </aside>
      </div>
      {endConfirm && (
        <EndBroadcastDialog control={control} onClose={() => setEndConfirm(false)} onStopped={stopPublishing} />
      )}
    </div>
  );
}

function drawImage(
  ctx: CanvasRenderingContext2D,
  src: HTMLVideoElement | HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number,
  cover: boolean,
) {
  const sw =
      src instanceof HTMLVideoElement ? src.videoWidth : src.naturalWidth,
    sh = src instanceof HTMLVideoElement ? src.videoHeight : src.naturalHeight;
  if (!sw || !sh) return;
  const scale = cover ? Math.max(w / sw, h / sh) : Math.min(w / sw, h / sh),
    dw = sw * scale,
    dh = sh * scale;
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  ctx.drawImage(src, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  ctx.restore();
}
